import { useState, useEffect, useMemo } from 'react'
import { getArkImageData, getRecordsCollection } from '@fs/zion-sls'

/**
 * Custom hook to manage history image data and collection data loading
 * @param {string} imageArk - The image ark identifier
 * @param {boolean} loading - Whether the main SLS data is still loading
 * @param {Object} state - The SLS state context
 * @returns {Object} { imageData, collection, isLoading, error }
 */
export const useHistoryImageData = (imageArk, loading, state) => {
  const [imageData, setImageData] = useState(null)
  const [collection, setCollection] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Memoize the data loading to prevent unnecessary API calls
  const shouldLoad = useMemo(() => !loading && imageArk && state, [loading, imageArk, state])

  useEffect(() => {
    let isMounted = true

    if (!shouldLoad) {
      return undefined
    }

    setIsLoading(true)
    setError(null)

    const loadData = async () => {
      try {
        const [imageResult, collectionResult] = await Promise.all([
          getArkImageData(imageArk),
          getRecordsCollection(state),
        ])

        if (isMounted) {
          setImageData(imageResult)
          setCollection(collectionResult)
          setIsLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
          setIsLoading(false)
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [shouldLoad, imageArk, state])

  return {
    imageData,
    collection,
    isLoading,
    error,
  }
}
